import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Building2, Car, Laptop, CreditCard, Plus, Trash2, Calculator, TrendingDown, DollarSign, Percent } from "lucide-react";
import { PeriodSelector, KPICard, IndicatorRow } from "@/components/UIComponents";
import { formatCurrency, formatPercent, MONTHS, CONFIG } from "@/lib/config";
import { toast } from "sonner";

// Types d'actifs avec leurs règles d'amortissement
const ASSET_TYPES = {
  vehicule: { label: "Véhicule", icon: Car, defaultYears: 5, method: "linear" },
  equipement: { label: "Équipement", icon: Building2, defaultYears: 7, method: "linear" },
  ameublement: { label: "Ameublement", icon: Building2, defaultYears: 5, method: "linear" },
  informatique: { label: "Informatique", icon: Laptop, defaultYears: 3, method: "linear" },
  publicite: { label: "Publicité", icon: Building2, defaultYears: 5, method: "linear" },
  batiment: { label: "Bâtiment", icon: Building2, defaultYears: 25, method: "linear" },
  autre: { label: "Autre", icon: Building2, defaultYears: 5, method: "linear" }
};

// Types de dettes
const DEBT_TYPES = {
  financement_auto: { label: "Financement auto", icon: Car },
  credit_bail: { label: "Crédit bail (leasing)", icon: Car },
  pret_vehicule: { label: "Prêt véhicule", icon: Car },
  pret_equipement: { label: "Prêt équipement", icon: Building2 },
  marge_credit: { label: "Marge de crédit", icon: CreditCard },
  pret_personnel: { label: "Prêt personnel", icon: DollarSign },
  autre: { label: "Autre dette", icon: CreditCard }
};

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export const ActifsEtDettes = () => {
  const [year, setYear] = useState(2025);
  const [month, setMonth] = useState(1);
  const [assets, setAssets] = useState([]);
  const [debts, setDebts] = useState([]);
  const [isAssetDialogOpen, setIsAssetDialogOpen] = useState(false);
  const [isDebtDialogOpen, setIsDebtDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newAsset, setNewAsset] = useState({
    type: "vehicule",
    name: "",
    purchaseDate: "",
    purchaseValue: "",
    residualValue: "0",
    depreciationYears: "5",
    department: "GLOBAL"
  });
  const [newDebt, setNewDebt] = useState({
    type: "pret_vehicule",
    name: "",
    startDate: "",
    originalAmount: "",
    currentBalance: "",
    interestRate: "",
    monthlyPayment: "",
    department: "GLOBAL"
  });

  // Charger depuis MongoDB au montage
  useEffect(() => {
    axios.get(`${API_URL}/api/configuration/actifs-dettes`).then(r => r.data)
      .then(d => {
        setAssets(d.assets || []);
        setDebts(d.debts || []);
        // Migrer depuis localStorage si présent
        const oldAssets = localStorage.getItem('financehub_assets');
        const oldDebts = localStorage.getItem('financehub_debts');
        if ((oldAssets || oldDebts) && (!d.assets?.length && !d.debts?.length)) {
          const parsedAssets = oldAssets ? JSON.parse(oldAssets) : [];
          const parsedDebts = oldDebts ? JSON.parse(oldDebts) : [];
          if (parsedAssets.length > 0 || parsedDebts.length > 0) {
            setAssets(parsedAssets);
            setDebts(parsedDebts);
            // Sauvegarder en base
            axios.put(`${API_URL}/api/configuration/actifs-dettes`, { assets: parsedAssets, debts: parsedDebts });
            localStorage.removeItem('financehub_assets');
            localStorage.removeItem('financehub_debts');
            toast.success("Vos données ont été migrées vers le serveur !");
          }
        }
      })
      .catch(() => {});

    // Charger les revenus annuels pour le ratio d'endettement
    axios.get(`${API_URL}/api/revente/donnees-annuelles/${new Date().getFullYear() - 1}`).then(r => r.data)
      .then(d => setRevenusAnnuels(d.revenus || 0))
      .catch(() => {});
  }, []);

  const [revenusAnnuels, setRevenusAnnuels] = useState(0);

  // Sauvegarder dans MongoDB
  const saveToMongo = async (newAssets, newDebts) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/configuration/actifs-dettes`, { assets: newAssets, debts: newDebts });
    } catch { toast.error("Erreur de sauvegarde"); }
    finally { setSaving(false); }
  };

  // Calculer l'amortissement mensuel pour un actif
  const calculateMonthlyDepreciation = (asset) => {
    const purchaseValue = parseFloat(asset.purchaseValue) || 0;
    const residualValue = parseFloat(asset.residualValue) || 0;
    const years = parseInt(asset.depreciationYears) || 5;
    return (purchaseValue - residualValue) / (years * 12);
  };

  // Calculer la valeur actuelle d'un actif
  const calculateCurrentValue = (asset) => {
    const purchaseDate = new Date(asset.purchaseDate);
    const now = new Date(year, month - 1);
    const monthsElapsed = (now.getFullYear() - purchaseDate.getFullYear()) * 12 + (now.getMonth() - purchaseDate.getMonth());
    const totalDepreciation = calculateMonthlyDepreciation(asset) * Math.max(0, monthsElapsed);
    const residualValue = parseFloat(asset.residualValue) || 0;
    const purchaseValue = parseFloat(asset.purchaseValue) || 0;
    return Math.max(residualValue, purchaseValue - totalDepreciation);
  };

  // Calculer les intérêts mensuels pour une dette
  const calculateMonthlyInterest = (debt) => {
    const balance = parseFloat(debt.currentBalance) || 0;
    const rate = parseFloat(debt.interestRate) || 0;
    return (balance * (rate / 100)) / 12;
  };

  // Calculer le capital mensuel
  const calculateMonthlyPrincipal = (debt) => {
    const payment = parseFloat(debt.monthlyPayment) || 0;
    const interest = calculateMonthlyInterest(debt);
    return Math.max(0, payment - interest);
  };

  // Totaux
  const totalAssetsValue = assets.reduce((sum, a) => sum + calculateCurrentValue(a), 0);
  const totalAssetsPurchase = assets.reduce((sum, a) => sum + (parseFloat(a.purchaseValue) || 0), 0);
  const totalMonthlyDepreciation = assets.reduce((sum, a) => sum + calculateMonthlyDepreciation(a), 0);
  const totalDebtsBalance = debts.reduce((sum, d) => sum + (parseFloat(d.currentBalance) || 0), 0);
  const totalMonthlyPayments = debts.reduce((sum, d) => sum + (parseFloat(d.monthlyPayment) || 0), 0);
  const totalMonthlyInterest = debts.reduce((sum, d) => sum + calculateMonthlyInterest(d), 0);
  const totalMonthlyPrincipal = debts.reduce((sum, d) => sum + calculateMonthlyPrincipal(d), 0);

  // Capitaux propres
  const equity = totalAssetsValue - totalDebtsBalance;

  const handleAddAsset = () => {
    if (!newAsset.name || !newAsset.purchaseDate || !newAsset.purchaseValue) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    const updatedAssets = [...assets, { ...newAsset, id: Date.now().toString() }];
    setAssets(updatedAssets);
    saveToMongo(updatedAssets, debts);
    setNewAsset({
      type: "vehicule",

      name: "",
      purchaseDate: "",
      purchaseValue: "",
      residualValue: "0",
      depreciationYears: "5",
      department: "GLOBAL"
    });
    setIsAssetDialogOpen(false);
    toast.success("Actif ajouté avec succès");
  };

  const handleAddDebt = () => {
    if (!newDebt.name || !newDebt.startDate || !newDebt.originalAmount || !newDebt.currentBalance) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    const updatedDebts = [...debts, { ...newDebt, id: Date.now().toString() }];
    setDebts(updatedDebts);
    saveToMongo(assets, updatedDebts);
    setNewDebt({
      type: "pret_vehicule",
      name: "",
      startDate: "",
      originalAmount: "",
      currentBalance: "",
      interestRate: "",
      monthlyPayment: "",
      department: "GLOBAL"
    });
    setIsDebtDialogOpen(false);
    toast.success("Dette ajoutée avec succès");
  };

  const handleDeleteAsset = (id) => {
    const updatedAssets = assets.filter(a => a.id !== id);
    setAssets(updatedAssets);
    saveToMongo(updatedAssets, debts);
    toast.info("Actif supprimé");
  };

  const handleDeleteDebt = (id) => {
    const updatedDebts = debts.filter(d => d.id !== id);
    setDebts(updatedDebts);
    saveToMongo(assets, updatedDebts);
    toast.info("Dette supprimée");
  };

  return (
    <div data-testid="actifs-dettes-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Actifs & Dettes</h1>
          <p className="text-slate-500 mt-1">Gestion des immobilisations et du financement - {MONTHS[month]} {year}</p>
        </div>
        <PeriodSelector year={year} setYear={setYear} month={month} setMonth={setMonth} />
      </div>

      {/* KPIs Résumé */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in delay-100">
        <KPICard
          title="Actifs totaux"
          value={formatCurrency(totalAssetsValue)}
          subtitle={`Valeur d'achat: ${formatCurrency(totalAssetsPurchase)}`}
          icon={Building2}
          color="emerald"
          description="Valeur actuelle de tous vos actifs après amortissement."
        />
        <KPICard
          title="Dettes totales"
          value={formatCurrency(totalDebtsBalance)}
          subtitle={`${debts.length} emprunts actifs`}
          icon={CreditCard}
          color="red"
          description="Solde total de toutes vos dettes et emprunts."
        />
        <KPICard
          title="Capitaux propres"
          value={formatCurrency(equity)}
          subtitle="Actifs - Dettes"
          icon={DollarSign}
          color={equity >= 0 ? "emerald" : "red"}
          description="Ce que vous possédez vraiment (Actifs - Dettes). C'est la valeur nette de l'entreprise."
        />
        <KPICard
          title="Amortissement mensuel"
          value={formatCurrency(totalMonthlyDepreciation)}
          subtitle="Dépréciation des actifs"
          icon={TrendingDown}
          color="amber"
          description="Montant mensuel de dépréciation comptable de vos actifs. C'est une dépense non-cash."
        />
      </div>

      {/* Paiements mensuels dettes */}
      <Card className="animate-fade-in delay-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Ventilation des paiements mensuels</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Paiements totaux</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalMonthlyPayments)}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 text-center">
              <p className="text-sm text-amber-600 mb-1">Portion intérêts</p>
              <p className="text-2xl font-bold text-amber-700">{formatCurrency(totalMonthlyInterest)}</p>
              <p className="text-xs text-amber-500 mt-1">{totalMonthlyPayments > 0 ? formatPercent((totalMonthlyInterest / totalMonthlyPayments) * 100) : '0%'} du paiement</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4 text-center">
              <p className="text-sm text-emerald-600 mb-1">Portion capital</p>
              <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalMonthlyPrincipal)}</p>
              <p className="text-xs text-emerald-500 mt-1">{totalMonthlyPayments > 0 ? formatPercent((totalMonthlyPrincipal / totalMonthlyPayments) * 100) : '0%'} du paiement</p>
            </div>
          </div>

          {/* Alerte Ratio d'Endettement */}
          {revenusAnnuels > 0 && (() => {
            const revenuMensuel = revenusAnnuels / 12;
            const ratioPct = (totalMonthlyPayments / revenuMensuel) * 100;
            const isGreen = ratioPct < 15;
            const isYellow = ratioPct >= 15 && ratioPct < 25;
            const isRed = ratioPct >= 25;
            const barWidth = Math.min(100, ratioPct);
            return (
              <div className={`mt-4 rounded-lg p-4 border ${isRed ? 'bg-red-50 border-red-200' : isYellow ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className={`text-sm font-semibold ${isRed ? 'text-red-700' : isYellow ? 'text-amber-700' : 'text-green-700'}`}>
                      {isRed ? '🔴' : isYellow ? '🟡' : '🟢'} Ratio d&apos;endettement — {ratioPct.toFixed(1)}% des revenus mensuels
                    </p>
                    <p className={`text-xs mt-0.5 ${isRed ? 'text-red-600' : isYellow ? 'text-amber-600' : 'text-green-600'}`}>
                      {formatCurrency(totalMonthlyPayments)}/mois ÷ {formatCurrency(revenuMensuel)}/mois (moy. {year - 1})
                      &nbsp;·&nbsp;
                      {isRed ? 'Attention : niveau d\'endettement élevé !' : isYellow ? 'Surveiller : approche du seuil critique (25%)' : 'Sain : bien en dessous du seuil d\'alerte (25%)'}
                    </p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className={`text-xs font-medium ${isRed ? 'text-red-600' : isYellow ? 'text-amber-600' : 'text-green-600'}`}>Seuils</p>
                    <p className="text-xs text-slate-500">🟢&lt;15% / 🟡15-25% / 🔴&gt;25%</p>
                  </div>
                </div>
                {/* Barre de progression */}
                <div className="w-full bg-white rounded-full h-3 border border-slate-200 overflow-hidden relative">
                  {/* Marqueurs */}
                  <div className="absolute top-0 left-[15%] h-full w-px bg-amber-400 opacity-60 z-10" />
                  <div className="absolute top-0 left-[25%] h-full w-px bg-red-400 opacity-60 z-10" />
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${isRed ? 'bg-red-500' : isYellow ? 'bg-amber-400' : 'bg-green-500'}`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>0%</span>
                  <span className="text-amber-500">15%</span>
                  <span className="text-red-500">25%</span>
                  <span>50%+</span>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Tabs Actifs / Dettes */}
      <Tabs defaultValue="actifs" className="animate-fade-in delay-300">
        <TabsList className="mb-4">
          <TabsTrigger value="actifs" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Actifs ({assets.length})
          </TabsTrigger>
          <TabsTrigger value="dettes" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Dettes ({debts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="actifs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Liste des actifs</CardTitle>
                <CardDescription>Véhicules, équipements, informatique, etc.</CardDescription>
              </div>
              <Dialog open={isAssetDialogOpen} onOpenChange={setIsAssetDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Ajouter un actif
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nouvel actif</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Type d&apos;actif</Label>
                        <Select value={newAsset.type} onValueChange={(v) => setNewAsset({ ...newAsset, type: v, depreciationYears: String(ASSET_TYPES[v].defaultYears) })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(ASSET_TYPES).map(([key, val]) => (
                              <SelectItem key={key} value={key}>{val.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Département</Label>
                        <Select value={newAsset.department} onValueChange={(v) => setNewAsset({ ...newAsset, department: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GLOBAL">Global</SelectItem>
                            {CONFIG.departments.map(d => (
                              <SelectItem key={d} value={d}>{CONFIG.department_names[d]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Nom / Description</Label>
                      <Input value={newAsset.name} onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })} placeholder="Ex: Camion Ford F-150 2024" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Date d&apos;achat</Label>
                        <Input type="date" value={newAsset.purchaseDate} onChange={(e) => setNewAsset({ ...newAsset, purchaseDate: e.target.value })} />
                      </div>
                      <div>
                        <Label>Valeur d&apos;achat ($)</Label>
                        <Input type="number" value={newAsset.purchaseValue} onChange={(e) => setNewAsset({ ...newAsset, purchaseValue: e.target.value })} placeholder="50000" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Valeur résiduelle ($)</Label>
                        <Input type="number" value={newAsset.residualValue} onChange={(e) => setNewAsset({ ...newAsset, residualValue: e.target.value })} placeholder="5000" />
                      </div>
                      <div>
                        <Label>Durée amortissement (années)</Label>
                        <Input type="number" value={newAsset.depreciationYears} onChange={(e) => setNewAsset({ ...newAsset, depreciationYears: e.target.value })} />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAssetDialogOpen(false)}>Annuler</Button>
                    <Button onClick={handleAddAsset}>Ajouter</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {assets.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Aucun actif enregistré. Cliquez sur &quot;Ajouter un actif&quot; pour commencer.</p>
              ) : (
                <div className="space-y-3">
                  {assets.map((asset) => {
                    const AssetIcon = ASSET_TYPES[asset.type]?.icon || Building2;
                    const currentValue = calculateCurrentValue(asset);
                    const monthlyDep = calculateMonthlyDepreciation(asset);
                    return (
                      <div key={asset.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-emerald-100 rounded-lg">
                            <AssetIcon className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{asset.name}</p>
                            <p className="text-xs text-slate-500">
                              {ASSET_TYPES[asset.type]?.label} • {asset.department} • Acheté le {new Date(asset.purchaseDate).toLocaleDateString('fr-CA')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="font-medium text-slate-900">{formatCurrency(currentValue)}</p>
                            <p className="text-xs text-slate-500">Amort: {formatCurrency(monthlyDep)}/mois</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAsset(asset.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dettes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Liste des dettes</CardTitle>
                <CardDescription>Prêts, marges de crédit, financement</CardDescription>
              </div>
              <Dialog open={isDebtDialogOpen} onOpenChange={setIsDebtDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Ajouter une dette
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nouvelle dette</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Type de dette</Label>
                        <Select value={newDebt.type} onValueChange={(v) => setNewDebt({ ...newDebt, type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(DEBT_TYPES).map(([key, val]) => (
                              <SelectItem key={key} value={key}>{val.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Département</Label>
                        <Select value={newDebt.department} onValueChange={(v) => setNewDebt({ ...newDebt, department: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GLOBAL">Global</SelectItem>
                            {CONFIG.departments.map(d => (
                              <SelectItem key={d} value={d}>{CONFIG.department_names[d]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Nom / Description</Label>
                      <Input value={newDebt.name} onChange={(e) => setNewDebt({ ...newDebt, name: e.target.value })} placeholder="Ex: Prêt camion Desjardins" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Date de début</Label>
                        <Input type="date" value={newDebt.startDate} onChange={(e) => setNewDebt({ ...newDebt, startDate: e.target.value })} />
                      </div>
                      <div>
                        <Label>Taux d&apos;intérêt annuel (%)</Label>
                        <Input type="number" step="0.01" value={newDebt.interestRate} onChange={(e) => setNewDebt({ ...newDebt, interestRate: e.target.value })} placeholder="6.5" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Montant original ($)</Label>
                        <Input type="number" value={newDebt.originalAmount} onChange={(e) => setNewDebt({ ...newDebt, originalAmount: e.target.value })} placeholder="40000" />
                      </div>
                      <div>
                        <Label>Solde actuel ($)</Label>
                        <Input type="number" value={newDebt.currentBalance} onChange={(e) => setNewDebt({ ...newDebt, currentBalance: e.target.value })} placeholder="35000" />
                      </div>
                    </div>
                    <div>
                      <Label>Paiement mensuel ($)</Label>
                      <Input type="number" value={newDebt.monthlyPayment} onChange={(e) => setNewDebt({ ...newDebt, monthlyPayment: e.target.value })} placeholder="800" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsDebtDialogOpen(false)}>Annuler</Button>
                    <Button onClick={handleAddDebt}>Ajouter</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {debts.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Aucune dette enregistrée. Cliquez sur &quot;Ajouter une dette&quot; pour commencer.</p>
              ) : (
                <div className="space-y-3">
                  {debts.map((debt) => {
                    const DebtIcon = DEBT_TYPES[debt.type]?.icon || CreditCard;
                    const monthlyInterest = calculateMonthlyInterest(debt);
                    const monthlyPrincipal = calculateMonthlyPrincipal(debt);
                    return (
                      <div key={debt.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-red-100 rounded-lg">
                            <DebtIcon className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{debt.name}</p>
                            <p className="text-xs text-slate-500">
                              {DEBT_TYPES[debt.type]?.label} • {debt.department} • {debt.interestRate}% annuel
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="font-medium text-red-600">{formatCurrency(parseFloat(debt.currentBalance))}</p>
                            <p className="text-xs text-slate-500">
                              Paiement: {formatCurrency(parseFloat(debt.monthlyPayment))}/mois
                              <span className="text-amber-600"> ({formatCurrency(monthlyInterest)} int.)</span>
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteDebt(debt.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ActifsEtDettes;
